import React, { useEffect, useRef, useState, FC, useCallback } from "react";
import {
  Application,
  Container,
  Texture,
  Assets,
  BitmapText,
  TextStyle,
} from "pixi.js";
import { PointLight } from "@pixi/lights";
import { OutlineFilter } from "pixi-filters";
import { Viewport } from "pixi-viewport";
import chalk from "chalk";
import { useLocation, useParams } from "wouter";

import { gsap } from "gsap";
import { PixiPlugin } from "gsap/PixiPlugin";

import _ from "lodash";
import { CategoriesSimulation } from "../../../simulation/CategoriesSimulation";
import { Cluster, IArtworkSprite, SortingArtwork } from "../../../interfaces";
import Loading from "../../../components/Loading";
import ZoomControl, { ZoomControlSize } from "../../../components/ZoomControl";
import BackToCollectionButton from "../../../components/buttons/BackToCollectionButton";
import {
  addArtworkSpriteEvents,
  createArtworkSprite,
} from "../../../utils/pixi";
import { isDebugMode } from "../../../config";
import CategorySlider, {
  CategorySliderRef,
} from "../../../components/CategorySlider";
import {
  calculateCentroid,
  calculateOptimalZoomAndCenter,
} from "../../../utils/math";
import audioManager from "../../../audio";
import ArtworkDetailView, { ArtworkDetailViewRef } from "../../ArtworkDetail";
import { useStore } from "../../../store/store";
import RoundButton from "../../../components/buttons/RoundButton";
gsap.registerPlugin(PixiPlugin);

const DOUBLE_CLICK_THRESHOLD = 300; // 300 ms
const MIN_ZOOM = 0.01;
const MAX_ZOOM = 8;

/**
 * Extracts the file extension from a given URL.
 * Returns the extension without the dot, e.g., "png", "jpeg".
 * If no extension is found, returns an empty string.
 * @param url The URL string to extract the extension from.
 */
const getFileExtension = (url: string): string => {
  try {
    const pathname = new URL(url).pathname;
    const lastDotIndex = pathname.lastIndexOf(".");
    if (lastDotIndex === -1) return "";
    return pathname.substring(lastDotIndex + 1).toLowerCase();
  } catch (error) {
    console.error("Invalid URL:", url);
    return "";
  }
};
const loadJSON = async (url: string) => {
  const response = await fetch(url);
  return await response.json();
};

const CategoriesView: FC = () => {
  const [, navigate] = useLocation();
  const params = useParams();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pixiAppRef = useRef<Application | null>(null);
  const viewportRef = useRef<Viewport | null>(null);
  const hasFetchedData = useRef(false);
  const simulationRef = useRef<CategoriesSimulation | null>(null);
  let lastClickTimeRef = useRef<number>(0);
  const artworkContainerRef = useRef<Container>(new Container());
  const debugLayerRef = useRef<Container>(new Container());

  // --------------------
  // Store Hooks
  // --------------------
  const { combinedTextures, setCombinedTextures, clearCombinedTextures } =
    useStore();

  const [texturesLoaded, setTexturesLoaded] = useState<boolean>(false);
  const [spritesSetup, setSpritesSetup] = useState<boolean>(false);
  const [isDataFetched, setIsDataFetched] = useState<boolean>(false);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const artworkRef = useRef<ArtworkDetailViewRef>(null);
  const [uuid, setUuid] = useState<string | null>(null);
  const [artworks, setArtworks] = useState<SortingArtwork[]>([]);
  const [artworkSprites, setArtworkSprites] = useState<IArtworkSprite[]>([]);
  const [isRendered, setIsRendered] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const sliderRef = useRef<CategorySliderRef>(null);

  const [dimensions] = useState<{
    width: number;
    height: number;
  }>({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [worldDimensions] = useState<{
    width: number;
    height: number;
  }>({
    width: window.innerWidth * 9,
    height: window.innerHeight * 9,
  });
  const [zoomLevel, setZoomLevel] = useState<number>(0);
  const [isAnimating] = useState<boolean>(false);
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasSimulationFinished, setHasSimulationFinished] =
    useState<boolean>(false);

  const hasData = artworks.length !== 0;

  useEffect(() => {
    artworkContainerRef.current.zIndex = 11;
    if (!hasFetchedData.current) {
      console.group("Sequence");
      console.info(chalk.cyan("[SEQ1]") + "Fetching data");
      fetchArtworkTimeline();
      hasFetchedData.current = true;
    }
  }, []);

  useEffect(() => {
    if (hasData && isDataFetched && !texturesLoaded && !simulationRef.current) {
      console.info(chalk.cyan("[SEQ2]") + "Loading textures");
      (async () => {
        await loadArtworkTextures();
      })();
    }
  }, [hasData, isDataFetched, texturesLoaded]);

  useEffect(() => {
    if (texturesLoaded) {
      console.info(chalk.cyan("[SEQ3]") + "Setting up sprites");
      (async () => {
        await setupSprites();
      })();
    }
  }, [texturesLoaded]);

  useEffect(() => {
    if (spritesSetup) {
      console.info(chalk.cyan("[SEQ4]") + "Running simulation");
      (async () => {
        await runSimulation();
      })();
    }
  }, [spritesSetup]);

  useEffect(() => {
    if (
      texturesLoaded &&
      spritesSetup &&
      hasSimulationFinished &&
      !pixiAppRef.current
    ) {
      console.info(chalk.cyan("[SEQ5]") + "Render pixi");
      console.groupEnd();
      (async () => {
        await renderPixi();
      })();
    }
  }, [texturesLoaded, hasSimulationFinished, spritesSetup, pixiAppRef.current]);

  useEffect(() => {
    const handleResize = () => {
      if (viewportRef.current && pixiAppRef.current) {
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;
        pixiAppRef.current.renderer.resize(newWidth, newHeight);
        viewportRef.current.resize(newWidth, newHeight, newWidth, newHeight);
        adjustViewport();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const cleanup = useCallback(() => {
    if (simulationRef.current) {
      simulationRef.current.instance.stop();
      simulationRef.current = null;
    }

    if (pixiAppRef.current) {
      pixiAppRef.current.destroy(true, {
        children: true,
        texture: true,
      });
      pixiAppRef.current = null;
    }

    if (viewportRef.current) {
      viewportRef.current.destroy();
      viewportRef.current = null;
    }
  }, []);

  const fetchArtworkTimeline = async () => {
    setIsDataFetched(true);
    setLoadingProgress(10);
    try {
      const response = await fetch(
        `https://admin.collectieconnectie.nl/api/v1/artworks/timeline`,
      );
      const result = await response.json();
      transformArtworkData(result);
      setLoadingProgress(30);
    } catch (error) {
      console.error("Error fetching artworks data:", error);
      setArtworks([]);
      setIsRendered(true);
      setLoadingProgress(100);
      setIsLoading(false);
    }
  };

  const transformArtworkData = (response: any) => {
    let collection = response.collection;
    if (collection.length !== 0) {
      const filtered = _.map(collection, (artwork) => {
        return {
          uuid: artwork.uuid,
          name: artwork.title,
          collection: artwork.collection,
          imageUrl: artwork.smallest_image_url,
          category: artwork.details.type,
          startYear: artwork.details.startYear, // Add the missing property
        };
      });
      setArtworks(filtered);
      setLoadingProgress(50);
    } else {
      setArtworks([]);
      setIsRendered(true);
      setLoadingProgress(100);
      setIsLoading(false);
    }
  };

  const runSimulation = useCallback(async () => {
    if (!artworks.length && !artworkSprites.length && !simulationRef.current)
      return;
    const simulation = new CategoriesSimulation(
      artworks,
      artworkSprites,
      worldDimensions,
    );
    simulation.onSimulationEnd = async (nodes) => {
      let _clusters: any = _.groupBy(nodes, "category");
      _clusters = _.map(_clusters, (cluster, key) => {
        const centroid = calculateCentroid(cluster);
        return {
          id: btoa(key),
          name: key,
          size: cluster.length,
          parent: undefined,
          children: cluster,
          centroid: centroid,
          categories: [key],
          angle: 0,
        };
      });
      setClusters(_clusters);

      setLoadingProgress(60);
      simulationRef.current = simulation;
      setLoadingProgress(90);
      setHasSimulationFinished(true);
    };
    simulation.run();
  }, [artworks, artworkSprites, simulationRef.current, worldDimensions]);

  /**
   * Load Artwork Textures from a single spritesheet JSON
   */
  const loadArtworkTextures = useCallback(async () => {
    try {
      // Start loading: set initial progress to 10%
      setLoadingProgress(10);

      // Check if combinedTextures are already loaded
      if (!_.isEmpty(combinedTextures)) {
        console.info(
          "Combined textures already loaded. Skipping texture loading.",
        );
        setTexturesLoaded(true);
        setLoadingProgress(90);
        return;
      }

      const baseUrl =
        "https://museumarnhem.ams3.digitaloceanspaces.com/spritesheet/";
      // Load the main spritesheet JSON
      const mainSpritesheetUrl = `${baseUrl}spritesheet.json`; // Update with your actual URL
      const mainData = await loadJSON(mainSpritesheetUrl);
      setLoadingProgress(20); // Update progress after main JSON is loaded

      if (!mainData.meta || !mainData.meta.related_sheets) {
        console.error("No related_sheets found in main JSON.");
        return;
      }

      const relatedSheets: string[] = mainData.meta.related_sheets;
      const totalSheets = relatedSheets.length;
      let loadedSheets = 0;

      const sheets: any[] = [];

      // Load each related spritesheet sequentially
      for (const sheetUrl of relatedSheets) {
        // Construct the full URL for the related spritesheet
        // Update with your actual base URL
        const fullSheetUrl = `${baseUrl}${sheetUrl}`;

        // Load the spritesheet using PixiJS Assets
        const spritesheet = await Assets.load(fullSheetUrl);
        sheets.push(spritesheet);

        loadedSheets += 1;

        const progressIncrement = 70 / totalSheets;
        setLoadingProgress((prev) =>
          Math.round(Math.min(prev + progressIncrement, 90)),
        );
      }

      // Combine all textures from the loaded spritesheets
      const allTextures: Record<string, Texture> = {};
      for (const sheet of sheets) {
        for (const [name, tex] of Object.entries(sheet.textures)) {
          // @ts-ignore
          allTextures[name] = tex;
        }
      }
      setCombinedTextures(allTextures); // Update the store
      setLoadingProgress(90); // Update progress after combining textures
      setTexturesLoaded(true);
    } catch (error) {
      console.error("Error loading spritesheet:", error);
      // Optionally, you can set progress to 100% and handle the error state
      setLoadingProgress(100);
      setIsLoading(false);
    }
  }, [combinedTextures, setCombinedTextures]);

  const setupSprites = useCallback(async () => {
    if (!combinedTextures) return;
    const artworkSprites = artworks.map((artwork) => {
      const extension = getFileExtension(artwork.imageUrl);
      const ext = extension || "jpg";
      const key = `${artwork.uuid}.${ext}`;
      const texture = combinedTextures[key] || Texture.WHITE;
      return createArtworkSprite(artwork.uuid, texture, false);
    });

    if (artworkSprites.length === 0) {
      console.warn("No valid artworks for sprites found.");
      return;
    }

    setArtworkSprites(artworkSprites);
    setSpritesSetup(true);
  }, [artworks, combinedTextures]);

  const renderPixi = useCallback(async () => {
    if (
      !simulationRef.current ||
      !artworkContainerRef.current ||
      !canvasRef.current
    ) {
      return;
    }
    const simulation = simulationRef.current;
    const pixiApp = new Application();
    PixiPlugin.registerPIXI(pixiApp);
    await pixiApp.init({
      canvas: canvasRef.current,
      width: dimensions.width,
      height: dimensions.height,
      resizeTo: window,
      resolution: window.devicePixelRatio || 1,
      antialias: true,
      autoDensity: true,
      useBackBuffer: true,
      background: "#1A1919",
    });

    pixiAppRef.current = pixiApp;

    if (viewportRef.current) return;
    const viewport = new Viewport({
      screenWidth: dimensions.width,
      screenHeight: dimensions.height,
      worldWidth: worldDimensions.width,
      worldHeight: worldDimensions.height,
      events: pixiApp.renderer.events,
    });

    viewportRef.current = viewport;
    pixiApp.stage.addChild(viewport);

    viewport.on("drag-start", () => {
      pixiApp.canvas.style.cursor = "grabbing";
    });
    viewport.on("drag-end", () => {
      pixiApp.canvas.style.cursor = "grab";
    });

    viewport
      .drag()
      .pinch()
      .wheel()
      .decelerate()
      .clampZoom({
        minWidth: (worldDimensions.width / 2) * MIN_ZOOM,
        maxWidth: (worldDimensions.width / 2) * MAX_ZOOM,
        minHeight: (worldDimensions.height / 2) * MIN_ZOOM,
        maxHeight: (worldDimensions.height / 2) * MAX_ZOOM,
        minScale: MIN_ZOOM,
        maxScale: MAX_ZOOM,
      });

    viewport.addChild(artworkContainerRef.current);

    if (isDebugMode) {
      viewport.addChild(debugLayerRef.current);
    }

    simulation.nodes.forEach((node: any) => {
      const { x, y, sprite } = node;
      addArtworkSpriteEvents(sprite, node, {
        onPointerTap: () => onArtworkTap(sprite, node),
        onSpriteOver: () => onArtworkMouseOver(sprite),
        onSpriteOut: () => onArtworkMouseOut(sprite),
      });
      sprite.x = x;
      sprite.y = y;
      artworkContainerRef.current.addChild(sprite);
    });

    zoomOutFitEntireLayout();

    setLoadingProgress(100);
    setIsRendered(true);
    setIsLoading(false);
  }, [
    canvasRef.current,
    simulationRef.current,
    artworkContainerRef.current,
    viewportRef.current,
    dimensions,
    worldDimensions,
    isDebugMode,
  ]);

  const zoomOutFitEntireLayout = useCallback(() => {
    if (!viewportRef.current || !artworkContainerRef.current) return;
    const viewport = viewportRef.current;
    // Zoom out to fit the entire layout
    const bounds = artworkContainerRef.current.getBounds();
    const layoutWidth = bounds.maxX - bounds.minX;
    const layoutHeight = bounds.maxY - bounds.minY;

    const scaleX = dimensions.width / layoutWidth;
    const scaleY = dimensions.height / layoutHeight;
    const zoomToFit = Math.min(scaleX, scaleY) * 0.7; // 0.7 to add some padding

    // Center the viewport on the layout
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;

    // Set zoom and center in one go
    viewport.setZoom(zoomToFit, true);
    viewport.moveCenter(centerX, centerY);

    // Update zoom state
    setZoomLevel(zoomToFit);
  }, [viewportRef.current, artworkContainerRef.current, dimensions]);

  const adjustViewport = useCallback(() => {
    if (
      !viewportRef.current ||
      !artworkContainerRef.current ||
      !simulationRef.current
    )
      return;
    const viewport = viewportRef.current;

    const bounds = artworkContainerRef.current.getBounds();
    const layoutWidth = bounds.maxX - bounds.minX;
    const layoutHeight = bounds.maxY - bounds.minY;

    const scaleX = worldDimensions.width / layoutWidth;
    const scaleY = worldDimensions.height / layoutHeight;
    const zoom = Math.min(scaleX, scaleY) * 0.9;

    const centerY = (bounds.minY + bounds.maxY) / 2;
    const minX = Math.min(
      ...simulationRef.current.nodes.map((node: any) => node.x),
    );

    setZoomLevel(zoom);
    viewport.moveCenter(minX, centerY);
    viewport.setZoom(zoom, true);
  }, [
    viewportRef,
    artworkContainerRef,
    simulationRef,
    worldDimensions.width,
    worldDimensions.height,
  ]);

  const onArtworkTap = (sprite: IArtworkSprite, node: any) => {
    const currentTime = Date.now();
    if (currentTime - lastClickTimeRef.current < DOUBLE_CLICK_THRESHOLD) {
      if (isAnimating) {
        lastClickTimeRef.current = currentTime;
        return;
      }
    }
    lastClickTimeRef.current = currentTime;
    setUuid(node.uuid);
  };

  const onArtworkMouseOver = (sprite: IArtworkSprite) => {
    if (isAnimating) return;
    sprite.filters = [
      new OutlineFilter({ thickness: 1.0, color: 0xffffff, alpha: 0.35 }),
    ];
  };

  const onArtworkMouseOut = (sprite: IArtworkSprite) => {
    if (isAnimating) return;
    sprite.filters = [];
  };

  const animateZoom = (
    viewport: Viewport | null,
    targetZoom: number,
    duration: number = 0.5,
  ): Promise<void> => {
    return new Promise((resolve) => {
      if (!viewport) {
        resolve();
        return;
      }

      const center = viewport.center;
      gsap.killTweensOf(zoomRef.current);

      gsap.to(zoomRef.current, {
        zoom: targetZoom,
        duration: duration,
        ease: "power2.out",
        onUpdate: () => {
          // @ts-ignore
          viewport.setZoom(zoomRef.current.zoom, { center });
        },
        onComplete: () => {
          resolve();
        },
      });
    });
  };

  const zoomRef = useRef({ zoom: zoomLevel });

  const handleCategoryClick = useCallback(
    async (category: string) => {
      audioManager.play("click");
      if (!viewportRef.current || !simulationRef.current || !clusters.length)
        return;

      const viewport = viewportRef.current;
      const cluster = clusters.find((_cluster) => _cluster.name === category);
      if (!cluster) return;

      setSelectedCategory(category);

      // Calculate the optimal zoom level for the selected cluster
      const { zoom: targetZoom } = calculateOptimalZoomAndCenter(
        cluster,
        viewport,
      );

      // If current zoom is greater than MIN_ZOOM, first zoom out
      if (zoomLevel > MIN_ZOOM + 0.01) {
        // Adding a small epsilon to account for floating-point precision
        await animateZoom(viewport, MIN_ZOOM, 0.8);
      }

      // Center the viewport on the new cluster
      viewport.moveCenter(cluster.centroid!.x, cluster.centroid!.y);

      // Animate zooming into the selected cluster
      await animateZoom(viewport, targetZoom, 0.8);

      // Update the zoom level state
      setZoomLevel(targetZoom);
    },
    [viewportRef, simulationRef, clusters, zoomLevel],
  );

  useEffect(() => {
    zoomRef.current.zoom = zoomLevel;
  }, [zoomLevel]);

  const handleZoomLevelChange = () => {
    console.log("Zoom level changed to:", zoomLevel);
  };

  useEffect(() => {
    if (!viewportRef.current) return;

    const viewport = viewportRef.current;

    const onZoomed = () => {
      const currentZoom = viewport.scale.x;
      setZoomLevel(currentZoom);
    };

    viewport.on("zoomed-end", onZoomed);

    return () => {
      viewport.off("zoomed-end", onZoomed);
    };
  }, [viewportRef.current]);

  const handleZoomChange = (value: number) => {
    console.log("handleZoomChange", value);
    const clampedZoom = Math.min(Math.max(value, MIN_ZOOM), MAX_ZOOM);

    setZoomLevel(clampedZoom);

    if (viewportRef.current) {
      animateZoom(viewportRef.current, clampedZoom);
    }

    handleZoomLevelChange();
  };

  const handleZoomIn = useCallback(() => {
    const newZoom = Math.min(zoomLevel + 0.5, MAX_ZOOM);
    setZoomLevel(newZoom);

    if (viewportRef.current) {
      animateZoom(viewportRef.current, newZoom);
    }

    handleZoomLevelChange();
  }, [zoomLevel]);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoomLevel - 0.5, MIN_ZOOM);
    setZoomLevel(newZoom);

    if (viewportRef.current) {
      animateZoom(viewportRef.current, newZoom);
    }
    handleZoomLevelChange();
  }, [zoomLevel]);

  const navigateBack = useCallback(async () => {
    if (
      selectedCategory &&
      viewportRef.current &&
      artworkContainerRef.current
    ) {
      await animateZoom(viewportRef.current, MIN_ZOOM, 0.8);
      setZoomLevel(MIN_ZOOM);
      setSelectedCategory(null);
      const bounds = artworkContainerRef.current.getBounds();
      viewportRef.current.moveCenter(
        bounds.minX + bounds.maxX / 2,
        bounds.minY + bounds.maxY / 2,
      );
    } else {
      navigate("/collection");
    }
  }, [navigate, selectedCategory, viewportRef.current, setSelectedCategory, artworkContainerRef.current]);


  return (
    <>
      <div className="z-20 h-full w-full absolute top-0 left-0 bg-primary select-none overscroll-none">
        <canvas ref={canvasRef} className="h-full w-full top-0 left-0" />
        <div className="h-full w-full top-0 left-0 z-30">
          {simulationRef.current?.categories && (
            <CategorySlider
              ref={sliderRef}
              categories={simulationRef.current.categories}
              selectedCategory={selectedCategory}
              onCategoryClick={handleCategoryClick}
            />
          )}

          <Loading
            isLoading={isLoading}
            progress={loadingProgress}
            message="Categorieën laden..."
          />

          {!isLoading && <></>}

          {!isLoading && (
            <ZoomControl
              size={ZoomControlSize.SMALL}
              zoomLevel={zoomLevel}
              onZoomChange={handleZoomChange}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              bottomClass={"bottom-20"}
            />
          )}

          {!isLoading && (
            <div className="top-8 left-4 fixed z-20">
              <RoundButton
                onClick={navigateBack}
                icon={selectedCategory ? "d-network-platform" : "d-arrow-left"}
              />
            </div>
          )}
        </div>
      </div>
      {uuid && (
        <ArtworkDetailView
          ref={artworkRef}
          uuid={uuid}
          onClose={() => {
            setUuid(null);
          }}
        />
      )}
    </>
  );
};

export default CategoriesView;
